import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { TemplateSettings, UserDocumentTemplateId } from 'packages/types'
import { actions as generalEditorActions } from 'builder/modules/generalEditor'
import ResumeEditorGuest from 'builder/components/DocumentEditor/ResumeEditorGuest'
import PreviewPanelGuest from 'builder/components/PreviewPanel/PreviewPanelGuest'
import {
  DocumentCustomizationModalSwitcher,
  getUpdatedSettings,
  prettifySize,
  RenderPreviewProp,
  useTemplateCustomization,
} from 'builder/components/DocumentCustomizationModal'
import { DocumentTypes, SPACING } from 'builder/modules/constants'
import { GuestResumeContext } from 'builder/context/GuestResumeContext'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import {
  ColorChangeHandler,
  FontChangeHandler,
  LayoutFormatChangeHandler,
  SizeChangeHandler,
  SpacingChangeHandler,
  TemplateChangeHandler,
  UpdateTemplateSettingsParams,
} from 'builder/modules/resumeEditor'
import PreviewReloadService from 'builder/services/PreviewReloadService'
import { PreviewGuest } from 'builder/components/Preview'
import { Container, LeftSide } from './styles'

// mock time added to mimic real builder behavior when on each
// change we wait for some time until changes are reflected in the preview
const mockLoadingTime = 500

export const ResumeEditorViewGuest = () => {
  const dispatch = useDispatch()
  const editorState = useTypedSelector(state => state.generalEditor)
  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false)
  const { resume, updateResumeFields, toggleEmailRequiredError } = useContext(GuestResumeContext)
  const { supportsCustomColor, selectedLayoutFormatOption, supportsNewCustomization } =
    useTemplateCustomization(resume)
  const { templatesSettings } = useTemplateCustomization(resume)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPagesNumber, setTotalPagesNumber] = useState(1)
  const [isSyncing, setIsSyncing] = useState(false)
  const isFirstResumeRender = useRef(true)

  useEffect(() => {
    dispatch(generalEditorActions.fetchEditorPayloadRequest())
  }, [dispatch])

  useEffect(() => {
    if (isFirstResumeRender.current) {
      isFirstResumeRender.current = false
      return
    }

    setIsSyncing(true)

    const timer = setTimeout(() => {
      PreviewReloadService.reloadResume(resume, false)
      setIsSyncing(false)
    }, mockLoadingTime)

    return () => {
      clearTimeout(timer)
    }
  }, [resume])

  const handleOpenCustomizationModal = useCallback(() => {
    setIsCustomizeModalOpen(true)
  }, [])

  const handleCloseCustomizationModal = useCallback(() => {
    setIsCustomizeModalOpen(false)
  }, [])

  const handleTemplateUpdate: TemplateChangeHandler = useCallback(
    id => {
      updateResumeFields({ template: id })
      trackInternalEvent('change_resume_template', { template: id })
    },
    [updateResumeFields],
  )

  const updateTemplateSettings = useCallback(
    <T extends UserDocumentTemplateId, S extends keyof TemplateSettings[T]>(
      params: UpdateTemplateSettingsParams<T, S>,
    ) => {
      const { templateName = resume?.template, settingsName, value } = params

      const updatedValue = getUpdatedSettings(
        { templateName, settingsName, value },
        templatesSettings,
      )

      if (!updatedValue) {
        return
      }

      updateResumeFields({ templateSettings: updatedValue })
    },
    [resume?.template, templatesSettings, updateResumeFields],
  )

  const handleColorSelect: ColorChangeHandler = useCallback(
    color => {
      if (color && supportsCustomColor && supportsNewCustomization) {
        updateTemplateSettings({
          settingsName: 'color',
          value: color,
        })
      } else {
        updateResumeFields({ color })
      }
      trackInternalEvent('change_resume_color', { template: resume?.template })
    },
    [
      updateResumeFields,
      supportsCustomColor,
      supportsNewCustomization,
      updateTemplateSettings,
      resume?.template,
    ],
  )

  const handleChangeLayoutFormat: LayoutFormatChangeHandler = useCallback(
    value => {
      if (typeof value !== 'string') return
      updateTemplateSettings({
        settingsName: 'format',
        value,
      })
      trackInternalEvent('change_page_size_format', {
        old_value: selectedLayoutFormatOption,
        new_value: value,
        template: resume?.template,
      })
    },
    [resume?.template, updateTemplateSettings, selectedLayoutFormatOption],
  )

  const handleFontChange: FontChangeHandler = useCallback(
    ({ value, newFontType, selectedPrimaryFont, selectedSecondaryFont, selectedTertiaryFont }) => {
      if (!resume?.template || typeof value !== 'string' || !newFontType) return

      const oldValue =
        newFontType === 'primaryFont'
          ? selectedPrimaryFont
          : newFontType === 'secondaryFont'
          ? selectedSecondaryFont
          : selectedTertiaryFont

      const eventFontType =
        newFontType === 'primaryFont'
          ? 'primary'
          : newFontType === 'secondaryFont'
          ? 'secondary'
          : 'tertiary'

      updateTemplateSettings({
        settingsName: newFontType,
        value,
      })

      trackInternalEvent('change_resume_font', {
        label: eventFontType,
        old_value: oldValue,
        new_value: value,
        template: resume?.template,
      })
    },
    [resume?.template, updateTemplateSettings],
  )

  const handleSizeChange: SizeChangeHandler = useCallback(
    (sizeNew: number, sizeOld: number) => {
      if (!resume?.template || typeof sizeNew !== 'number') return
      updateTemplateSettings({
        settingsName: 'fontSize',
        value: sizeNew,
      })
      trackInternalEvent('change_resume_font_size', {
        old_value: prettifySize(sizeOld),
        new_value: prettifySize(sizeNew),
        template: resume.template,
      })
    },
    [resume?.template, updateTemplateSettings],
  )

  const handleSpacingChange: SpacingChangeHandler = (spacingNew: number, spacingOld: number) => {
    const normalize = (value: number) => Math.min(Math.max(value, SPACING.min), SPACING.max)

    if (supportsNewCustomization) {
      if (typeof spacingNew !== 'number') return
      updateTemplateSettings({
        settingsName: 'lineSpacing',
        value: spacingNew,
      })
    } else {
      updateResumeFields({ spacing: normalize(spacingNew) })
    }

    trackInternalEvent('change_line_spacing', {
      label: 'templates',
      direction: spacingNew > spacingOld ? 'increase' : 'decrease',
      old_value: spacingOld,
      new_value: spacingNew,
    })
  }

  const handleDownloadDocument = useCallback(() => {
    handleCloseCustomizationModal()
    toggleEmailRequiredError()
  }, [handleCloseCustomizationModal, toggleEmailRequiredError])

  const onPaginationChange = (state: { currentPage: number; totalPagesCount: number }) => {
    const { currentPage, totalPagesCount } = state
    setCurrentPage(currentPage + 1)
    setTotalPagesNumber(totalPagesCount)
  }

  const handleDownloadButtonMenuOpen = useCallback(() => {
    trackInternalEvent('click_download_resume_formats', {
      source: isCustomizeModalOpen ? 'full_screen_resume_preview' : 'resume_editor',
      template: resume?.template,
    })
  }, [resume?.template, isCustomizeModalOpen])

  const renderPreview: RenderPreviewProp = ({ height, width }) => {
    return (
      <PreviewGuest
        {...{
          height,
          width,
          currentDocument: resume,
          onPaginationChange,
          handleUnlockClick: toggleEmailRequiredError,
          analyticsLabel: 'customization_modal',
        }}
      />
    )
  }

  return (
    <Container>
      <LeftSide>
        <ResumeEditorGuest />
      </LeftSide>
      <PreviewPanelGuest
        {...{
          handleFullSize: handleOpenCustomizationModal,
          currentPage,
          totalPagesNumber,
          onPaginationChange,
          isSyncing,
          handleDownloadButtonMenuOpen,
        }}
      />
      {isCustomizeModalOpen && resume && (
        <DocumentCustomizationModalSwitcher
          type={DocumentTypes.resume}
          isSyncing={isSyncing}
          isDownloading={false}
          currentDocument={resume}
          editorState={editorState}
          onClose={handleCloseCustomizationModal}
          handleDownloadDocument={handleDownloadDocument}
          updateTemplate={handleTemplateUpdate}
          handleColorSelect={handleColorSelect}
          handleChangeLayoutFormat={handleChangeLayoutFormat}
          handleFontChange={handleFontChange}
          handleSizeChange={handleSizeChange}
          handleSpacingChange={handleSpacingChange}
          renderPreview={renderPreview}
          handleDownloadButtonMenuOpen={handleDownloadButtonMenuOpen}
          mobilePanelSwitcherItems={[]}
        />
      )}
    </Container>
  )
}
